import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useI18N } from "@src/language/language-context";
import { updateShowOtaConfirmFirmwareInstallationModal } from "@src/reducers/your-reducer";
import { formatBytes } from "@src/cloud-connect/utils/format-bytes";
import { RootState } from "@src/reducers/store";
import { ActiveInstallationProcessPayload } from "@src/types/ota-update";
import { denyInstallationProcessUseCase } from "@src/cloud-connect/ota-update/use-cases/deny-installation-process-use-case";
import { approveInstallationProcessUseCase } from "@src/cloud-connect/ota-update/use-cases/approve-installation-process-use-case";
import {
  errorSnackbar,
  successSnackbar,
} from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import {
  OTA_OBJECT_TYPE,
  type InstallAndActionBlock,
} from "@src/cloud-connect/ota-update/constants";
import { useFeatureFlag } from "@src/hooks/use-feature-flag";

export const useOtaConfirmFirmwareInstallationModal = (
  data: ActiveInstallationProcessPayload,
  onClose: () => void,
  setInstallAndActionBlock: React.Dispatch<
    React.SetStateAction<InstallAndActionBlock | null>
  >,
) => {
  const dispatch = useDispatch();
  const i18n = useI18N();

  const source = useSelector(
    (state: RootState) => state.yourSlice?.connectionView?.source,
  );
  const isDebugLoggingEnabled = useFeatureFlag("allow-debug", false);

  const isBackupInstallation =
    data?.firmware?.type === OTA_OBJECT_TYPE.FIRMWARE_BACKUP;
  const [isApproveLoading, setIsApproveLoading] = useState(false);

  const texts = {
    closeModalAriaLabel: isBackupInstallation
      ? i18n(
          "modules.otaConfirmFirmwareInstallationModal.closeModalAriaLabelBackup",
        )
      : i18n(
          "modules.otaConfirmFirmwareInstallationModal.closeModalAriaLabelFirmware",
        ),
    title: isBackupInstallation
      ? i18n("modules.otaConfirmFirmwareInstallationModal.titleBackup")
      : i18n("modules.otaConfirmFirmwareInstallationModal.titleFirmware"),
    firmwareSectionTitle: isBackupInstallation
      ? i18n("modules.otaConfirmFirmwareInstallationModal.backupSectionTitle")
      : i18n(
          "modules.otaConfirmFirmwareInstallationModal.firmwareSectionTitle",
        ),
    uploadedAtLabel: i18n(
      "modules.otaConfirmFirmwareInstallationModal.uploadedAtLabel",
    ),
    machineSectionTitle: i18n(
      "modules.otaConfirmFirmwareInstallationModal.machineSectionTitle",
    ),
    machineIdLabel: i18n(
      "modules.otaConfirmFirmwareInstallationModal.machineIdLabel",
    ),
    machineIpLabel: i18n(
      "modules.otaConfirmFirmwareInstallationModal.machineIpLabel",
    ),
    targetPathLabel: i18n(
      "modules.otaConfirmFirmwareInstallationModal.targetPathLabel",
    ),
    foldersSectionTitle: isBackupInstallation
      ? i18n(
          "modules.otaConfirmFirmwareInstallationModal.backupFoldersSectionTitle",
        )
      : i18n(
          "modules.otaConfirmFirmwareInstallationModal.firmwareFoldersSectionTitle",
        ),
    warningText: isBackupInstallation
      ? i18n("modules.otaConfirmFirmwareInstallationModal.warningTextBackup")
      : i18n("modules.otaConfirmFirmwareInstallationModal.warningTextFirmware"),
    denyButtonText: i18n(
      "modules.otaConfirmFirmwareInstallationModal.denyButtonText",
    ),
    approveButtonText: i18n(
      "modules.otaConfirmFirmwareInstallationModal.approveButtonText",
    ),
    denySuccessMessage: i18n(
      "modules.otaConfirmFirmwareInstallationModal.denySuccessMessage",
    ),
    denyFailureMessage: i18n(
      "modules.otaConfirmFirmwareInstallationModal.denyFailureMessage",
    ),
    denyErrorMessage: isBackupInstallation
      ? i18n(
          "modules.otaConfirmFirmwareInstallationModal.denyErrorMessageBackup",
        )
      : i18n(
          "modules.otaConfirmFirmwareInstallationModal.denyErrorMessageFirmware",
        ),
    approveSuccessMessage: i18n(
      "modules.otaConfirmFirmwareInstallationModal.approveSuccessMessage",
    ),
    approveFailureMessage: i18n(
      "modules.otaConfirmFirmwareInstallationModal.approveFailureMessage",
    ),
    approveErrorMessage: isBackupInstallation
      ? i18n(
          "modules.otaConfirmFirmwareInstallationModal.approveErrorMessageBackup",
        )
      : i18n(
          "modules.otaConfirmFirmwareInstallationModal.approveErrorMessageFirmware",
        ),
  };

  const firmware = {
    fileName: data?.firmware?.filename,
    fileSize: formatBytes(data?.firmware?.size_bytes ?? 0),
    uploadedAt: data?.firmware?.uploaded_at,
  };

  const machine = {
    name: source?.name,
    id: source?.source_id,
    ip: source?.dsn,
  };

  const handleCloseModal = () => {
    onClose();
    dispatch(updateShowOtaConfirmFirmwareInstallationModal(false));
  };

  const handleDeny = async () => {
    if (!data?.update_id) {
      errorSnackbar(texts.denyErrorMessage);
      return;
    }

    try {
      const response = await denyInstallationProcessUseCase(data.update_id);
      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Response from denyInstallationProcessUseCase:",
          response,
        );
      if (response.success) {
        successSnackbar(texts.denySuccessMessage);
        handleCloseModal();
        setInstallAndActionBlock(null);
      } else {
        console.error(response.error);
        errorSnackbar(`${texts.denyFailureMessage}`);
      }
    } catch (error) {
      console.error(error);
      errorSnackbar(texts.denyErrorMessage);
    }
  };

  const handleApprove = async () => {
    if (!data?.update_id) {
      errorSnackbar(texts.approveErrorMessage);
      return;
    }

    setIsApproveLoading(true);

    try {
      const response = await approveInstallationProcessUseCase(data.update_id);
      isDebugLoggingEnabled &&
        console.log(
          "OTA_DEBUG: Response from approveInstallationProcessUseCase:",
          response,
        );
      if (response.success) {
        successSnackbar(texts.approveSuccessMessage);
        handleCloseModal();
        return;
      }

      console.error(response.error);
      errorSnackbar(`${texts.approveFailureMessage}`);
      setIsApproveLoading(false);
    } catch (error) {
      console.error(error);
      errorSnackbar(texts.approveErrorMessage);
      setIsApproveLoading(false);
    }
  };

  return {
    firmware,
    machine,
    folders: data?.archive_structure_root_level,
    texts,
    handleCloseModal,
    handleDeny,
    handleApprove,
    isApproveLoading,
    target_path: data?.target_path,
  };
};
